/**
 * Contract Detail Component
 *
 * Displays contract details with tabs for line items, addons, discounts, and taxes
 */

import type { ContractResponse } from '@monorepo/shared/schemas/contracts'
import { Loader2, Pause, Play, X } from 'lucide-react'
import { useState } from 'react'
import {
  SubtleTabs,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  useActivateContract,
  useCancelContract,
  useContract,
  usePauseContract,
} from '../hooks/useContracts'
import { AddonsTab } from './tabs/AddonsTab'
import { DiscountsTab } from './tabs/DiscountsTab'
import { LineItemsTab } from './tabs/LineItemsTab'
import { OverviewTab } from './tabs/OverviewTab'
import { TaxesTab } from './tabs/TaxesTab'

interface ContractDetailProps {
  contractId: string
}

export function ContractDetail({ contractId }: ContractDetailProps) {
  const { data: contract, isLoading, error } = useContract(contractId)
  const [activeTab, setActiveTab] = useState('overview')

  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false)

  const { mutate: activateContract } = useActivateContract()
  const { mutate: pauseContract } = usePauseContract()
  const { mutate: cancelContract } = useCancelContract()

  const getStatusVariant = (status: ContractResponse['status']) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'draft':
      case 'pending_signature':
        return 'secondary'
      case 'suspended':
        return 'outline'
      case 'terminated':
      case 'expired':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  const handlePauseConfirm = () => {
    pauseContract(contractId)
    setPauseConfirmOpen(false)
  }

  const handleCancelConfirm = () => {
    cancelContract({ id: contractId, reason: 'User requested cancellation' })
    setCancelConfirmOpen(false)
  }

  const handleResumeConfirm = () => {
    activateContract(contractId)
    setResumeConfirmOpen(false)
  }

  const tabs = [
    { value: 'overview', label: 'Overview' },
    { value: 'line-items', label: 'Line Items' },
    { value: 'addons', label: 'Add-ons' },
    { value: 'discounts', label: 'Discounts' },
    { value: 'taxes', label: 'Taxes' },
  ]

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground'>Loading contract details...</p>
        </div>
      </div>
    )
  }

  if (error || !contract) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <p className='text-red-600 font-medium'>Failed to load contract</p>
          <p className='text-sm text-muted-foreground mt-1'>
            {error?.message || 'Contract not found'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header Section */}
      <Card>
        <CardHeader>
          <div className='flex items-start justify-between'>
            <div className='space-y-2'>
              <CardTitle className='text-2xl'>{contract.name}</CardTitle>
              {contract.description && (
                <p className='text-sm text-muted-foreground'>
                  {contract.description}
                </p>
              )}
              <div className='flex items-center gap-2'>
                <Badge variant={getStatusVariant(contract.status)}>
                  {contract.status
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </Badge>
                <span className='text-sm text-muted-foreground'>
                  {contract.currency} • {contract.billing_interval}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex gap-2'>
              {contract.status === 'draft' && (
                <Button
                  variant='default'
                  onClick={() => activateContract(contractId)}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Activate
                </Button>
              )}

              {contract.status === 'active' && (
                <>
                  <Button
                    variant='outline'
                    onClick={() => setPauseConfirmOpen(true)}
                  >
                    <Pause className='w-4 h-4 mr-2' />
                    Pause
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => setCancelConfirmOpen(true)}
                  >
                    <X className='w-4 h-4 mr-2' />
                    Cancel
                  </Button>
                </>
              )}

              {contract.status === 'suspended' && (
                <Button
                  variant='default'
                  onClick={() => setResumeConfirmOpen(true)}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Resume
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs Section */}
      <Card>
        <CardContent className='p-0'>
          <div className='border-b'>
            <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
              <SubtleTabsList>
                {tabs.map(tab => (
                  <SubtleTabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </SubtleTabsTrigger>
                ))}
              </SubtleTabsList>
            </SubtleTabs>
          </div>

          <div className='p-6'>
            {activeTab === 'overview' && <OverviewTab contract={contract} />}
            {activeTab === 'line-items' && (
              <LineItemsTab contractId={contractId} />
            )}
            {activeTab === 'addons' && <AddonsTab contractId={contractId} />}
            {activeTab === 'discounts' && (
              <DiscountsTab contractId={contractId} />
            )}
            {activeTab === 'taxes' && <TaxesTab contractId={contractId} />}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={pauseConfirmOpen}
        onOpenChange={setPauseConfirmOpen}
        title='Pause Contract'
        description={`Are you sure you want to pause the contract "${contract.name}"? Billing will be suspended until resumed.`}
        confirmText='Pause Contract'
        onConfirm={handlePauseConfirm}
      />

      <ConfirmationDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title='Cancel Contract'
        description={`Are you sure you want to cancel the contract "${contract.name}"? This action cannot be undone.`}
        confirmText='Cancel Contract'
        onConfirm={handleCancelConfirm}
      />

      <ConfirmationDialog
        open={resumeConfirmOpen}
        onOpenChange={setResumeConfirmOpen}
        title='Resume Contract'
        description={`Are you sure you want to resume the contract "${contract.name}"? Billing will resume according to the contract terms.`}
        confirmText='Resume Contract'
        onConfirm={handleResumeConfirm}
      />
    </div>
  )
}
